import { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface AdjustProductProps {
  visible: boolean;
  onHide: () => void;
  product: any;
  onSuccess: (posId: number[]) => void; 
  setLoading: (loading: boolean) => void;
}

interface SourceType {
  id: number;
  name: string;
}

const AdjustProduct: React.FC<AdjustProductProps> = ({ visible, onHide, product,onSuccess,setLoading }) => {
  const toast = useRef<Toast>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [selectedSourceType, setSelectedSourceType] = useState<SourceType | null>(null);
  const [sourceType, setSourceType] = useState<SourceType[]>([]);
  const [batchCode, setBatchCode] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const loadSourceTypeDropdown = async () => {
    try {
      const sourcetyperesponse = await posService.GetAdjustSourceType();
        if (Array.isArray(sourcetyperesponse)) {
        setSourceType(sourcetyperesponse);
      }
    }catch (error) {
      console.error("Failed to load dropdown data", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load dropdown data",
      });
    }
  }

  useEffect(() => {
      if (visible) {
        loadSourceTypeDropdown();
      }
      if (!visible) {
      setQuantity(0);
      setSelectedSourceType(null);
      setBatchCode("");
      setReason("");
      setNote("");
    }
    }, [visible]);

  const handleSubmit = async () => {
   
    if (!quantity || quantity <= 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a valid quantity.",
      });
      return;
    }

     if (!selectedSourceType) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a source type.",
      });
      return;
    }

    if (!reason) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a Reason.",
      });
      return;
    }

    if (!batchCode) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a batch code or document log.",
      });
      return;
    }
  if (!note) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please enter a Note.",
      });
      return;
    }
    if (product.measurementTypeId === 4 && !Number.isInteger(quantity)) {
        toast.current?.show({
          severity: "warn",
          summary: "Validation Error",
          detail: "Decimal quantities are not allowed for this product.",
        });
        return;
      }


    if (quantity > product.currentStock) {
  toast.current?.show({
    severity: "warn",
    summary: "Validation Error",
    detail: `Entered quantity exceeds available stock (${product.currentStock}).`,
  });
  return;
}

     const payload = {
    ProductId: product.productId, 
    Posid: product.posId,      
    Quantity: quantity,
    SourceTypeId: selectedSourceType?.id,     
    Reason:reason,
    DocumentDetails: batchCode,
    Note: note
  };

  try {
    setLoading(true);
    const response = await posService.AddPosAdjustproducts(payload);
    if (response && response.isSuccess) {
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response.message || "Inward data submitted successfully",
      });
      onHide(); // Close modal on success
      onSuccess([product.posId]); 
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: response?.message || "Failed to submit Adjust data",
      });
    }
  } catch (error) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: "An error occurred during submission",
    });
  }finally {
    setLoading(false); // Always stop loading
  }
};

  return (
    <>
      <Toast ref={toast} />
      <Dialog header="Adjust Product" visible={visible} onHide={onHide} breakpoints={{ '960px': '90vw' }} style={{ width: '30vw' }} modal>
        <div className="p-inputtext p-component bg-gray-100 py-2 px-3 rounded">
          {product?.productName || '-'}
        </div>
        <div className="p-fluid">
        <div className="mb-3">
          <label className="font-semibold text-sm block mb-1">Quantity <span className="text-danger">*</span></label>
          <InputText type="number" min="0" keyfilter="pnum" onChange={(e) => setQuantity(+e.target.value)} 
          style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
        </div>

        <div className="mb-3">
          <label className="font-semibold text-sm block mb-1">Source Type <span className="text-danger">*</span></label>
          <Dropdown 
            value={selectedSourceType} 
            onChange={(e) => setSelectedSourceType(e.value)} 
            options={sourceType} 
            optionLabel="name" 
            placeholder="Select a SourceType"  
            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
        </div>

        <div className="mb-3">
          <label className="font-semibold text-sm block mb-1">Reason <span className="text-danger">*</span></label>
          <InputText value={reason} onChange={(e) => setReason(e.target.value)} 
          style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
        </div>

        <div className="mb-3">
        <label className="font-semibold text-sm block mb-1">Batch Code / Document Log <span className="text-danger">*</span></label>
        <InputText value={batchCode} onChange={(e) => setBatchCode(e.target.value)} 
        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
        />
        </div>

        <div className="mb-3">
          <label className="font-semibold text-sm block mb-1">Note <span className="text-danger">*</span></label>
          <InputText value={note} onChange={(e) => setNote(e.target.value)} 
           maxLength={255}
          style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
           <small style={{ display: 'block', marginTop: '5px', color: '#6c757d' }}>
            {note.length} / 255
          </small>
        </div>

          <Button label="Submit" className="mt-4" onClick={handleSubmit} />
        </div>
      </Dialog>
    </>
  );
};

export default AdjustProduct;
